import type { MessageKey } from '@bitwize/i18n-messages';

import { localePath, useI18n } from '../i18n';
import { Container } from '../ui';
import { BrandLogo } from './BrandLogo';
import { PRIMARY_NAV, type NavItem } from './navigation';

const NAV_SPLIT = Math.ceil(PRIMARY_NAV.length / 2);

/**
 * The mock's two labelled footer columns. Both are filled from `PRIMARY_NAV`
 * rather than hand-listed, so a route added or hidden there appears or
 * disappears here too — the mock's own "About" and "Careers" entries are
 * deliberately not reproduced, because no such page exists to link to and a
 * footer full of dead links is worse than a shorter one.
 */
const COLUMNS: ReadonlyArray<{ headingKey: MessageKey; items: readonly NavItem[] }> = [
  { headingKey: 'common.footer.navigate', items: PRIMARY_NAV.slice(0, NAV_SPLIT) },
  { headingKey: 'common.footer.company', items: PRIMARY_NAV.slice(NAV_SPLIT) },
];

export function Footer() {
  const { locale, t, formatNumber } = useI18n();

  return (
    <footer className="hs-footer mt-auto text-[var(--hs-brand-200)]">
      <Container>
        <div className="flex flex-col gap-10 py-16">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-[1.4fr_1fr_1fr]">
            <div className="max-w-sm">
              {/*
                The full lockup, stacked — the vertical mark-over-wordmark
                composition the footer's height can afford and Header's single
                line can't.

                This is the one surface with room for the `digits` cut, so
                it is the one that gets it — and 240px is not a preference, it
                is the floor. Below roughly 200 the 13 columns of digits stop
                resolving and the bowls' counters close up, so the mark reads
                as a smear of numbers rather than as a B. Every other surface
                takes the solid cut for exactly that reason.

                The artwork this replaces needed a hand-inked `_dark` PNG here,
                because its "bit" and "SOFTWARE COMPANY" were set in #092240
                and this footer runs brand-900 → neutral-950. `BrandMark` reads
                `--hs-color-mark-*` instead, and the dark theme already lifts
                those clear of the ink — so there is no second file left to
                keep in step with the first.
              */}
              <BrandLogo
                orientation="stacked"
                markVariant="digits"
                markClassName="w-[240px] max-w-full"
                className="font-heading text-lg font-semibold text-white"
              />
              <p className="mt-3 max-w-[34ch] text-sm opacity-85">{t('common.brand.tagline')}</p>
            </div>

            {COLUMNS.map((column) => (
              <nav key={column.headingKey} aria-label={t(column.headingKey)}>
                {/*
                  The one uppercase, wide-tracked line on the site — the mock
                  shouts its footer column headings and nothing else, which is
                  why the tracking and the transform are local here rather
                  than baked into `.hs-meta`.
                */}
                <h2 className="hs-meta mb-4 uppercase tracking-[0.06em] text-white">
                  {t(column.headingKey)}
                </h2>
                {/*
                  `py-1.5` on the links, not on the column: a bare 14px line
                  is a 20px tap target, and on a phone these six links are
                  the last navigation on the page. The padding takes each to
                  32px — past the 24px WCAG 2.5.8 asks for — and is the
                  mock's own `padding: 6px 0`, so the footer's rhythm is
                  unchanged on a desktop where none of this matters.
                */}
                <div className="flex flex-col gap-0.5">
                  {column.items.map((item) => (
                    <a
                      key={item.path}
                      href={localePath(locale, item.path)}
                      className="py-1.5 text-sm opacity-85 transition-opacity hover:text-white hover:opacity-100"
                    >
                      {t(item.labelKey)}
                    </a>
                  ))}
                </div>
              </nav>
            ))}
          </div>

          <p className="border-t border-white/10 pt-6 text-sm opacity-80">
            {/*
              The year is formatted through the locale formatter rather than
              interpolated raw, so the numbering system is a decision made once
              in packages/i18n-messages and not per component.
            */}
            {t('common.footer.copyright', {
              year: formatNumber(new Date().getFullYear(), { useGrouping: false }),
            })}
          </p>
        </div>
      </Container>
    </footer>
  );
}
